import { useState, useCallback } from 'react';
import Header from '@/components/Header/Header';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getGetTravellerBearersQueryKey,
  getGetTravellerByIDQueryKey,
  getGetTravellersForDepartmentQueryKey,
  getGetTravellersPerOrganizationQueryKey,
  TravellerWithCollectionMembershipsDTO,
  useLinkTravellerBobcatAccount,
  usePurchaseProductSetForCollectionMembers,
  usePurchaseProductSetForExternalRecipients,
  useTravelCardClaimAnonymous,
} from '@/api/trafikportalen';
import { CenteredColumn } from '@/components/CenteredColumn';
import { TravellerSelection } from '@/components/BuyTicket/TravellerSelection';
import { TicketSelection } from '@/components/BuyTicket/TicketSelection';
import { PaymentSelection } from '@/components/BuyTicket/PaymentSelection';
import Button from '@/components/core/Button';
import CheckBox from '@/components/core/Checkbox';
import { toast } from '@/hooks/useToast';
import { IconCheck } from '@/icons';
import { ProductSet } from '@/types/buyTicket';
import { useTenantID } from '@/hooks/useTenantID';
import { OrganizationCreditInfo } from '@/components/OrganizationCreditInfo';
import { getApiErrorMessage } from '@/helpers/apiError';
import { hasAnyDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { useCanBuyTickets } from '@/hooks/useCanBuyTickets';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { DocumentHead } from '@/components/core/DocumentHead';
import { TemporaryTravellerData } from '@/components/BuyTicket/TemporaryTravellerForm';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBanner } from '@/components/core/ErrorBanner';

type SearchParams = {
  travellerId?: string;
};

export const Route = createFileRoute('/$tenantId/_layout/buy-ticket/')({
  component: BuyTicket,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      travellerId: search.travellerId as string | undefined,
    };
  },
  beforeLoad: ({ params }) => {
    if (!hasAnyDepartmentWriteAccess()) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function BuyTicket() {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { remainingCredit, hasNoCreditLimit } = useOrgCreditInfo();
  const canBuyTickets = useCanBuyTickets();

  const { travellerId } = Route.useSearch();

  const [selectedTraveller, setSelectedTraveller] = useState<
    TravellerWithCollectionMembershipsDTO | undefined
  >(undefined);
  const [selectedBearer, setSelectedBearer] = useState<string>('');
  const [selectedProductSet, setSelectedProductSet] = useState<
    ProductSet | undefined
  >(undefined);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [isTemporaryTraveller, setIsTemporaryTraveller] = useState(false);
  const [temporaryTravellerData, setTemporaryTravellerData] = useState<
    TemporaryTravellerData | undefined
  >(undefined);
  const [staticTicket, setStaticTicket] = useState(false);
  const [bobcatEmail, setBobcatEmail] = useState<string>('');
  const [saveBobcatEmail, setSaveBobcatEmail] = useState<boolean>(true);
  const [sendEmailNotification, setSendEmailNotification] =
    useState<boolean>(true);

  const creditOverLimit =
    !hasNoCreditLimit &&
    selectedProductSet &&
    selectedProductSet.amount > remainingCredit;

  const purchaseTicket = usePurchaseProductSetForCollectionMembers();
  const purchaseTicketForExternal =
    usePurchaseProductSetForExternalRecipients();
  const travelCardClaimAnonymous = useTravelCardClaimAnonymous();
  const linkTravellerBobcatAccount = useLinkTravellerBobcatAccount();

  const isPurchasing =
    purchaseTicket.isPending ||
    purchaseTicketForExternal.isPending ||
    travelCardClaimAnonymous.isPending;

  const handlePurchaseTicket = useCallback(async () => {
    if (!selectedProductSet || !selectedDepartment) {
      return;
    }

    if (isPurchasing) {
      return;
    }

    if (temporaryTravellerData) {
      try {
        let bearerId = temporaryTravellerData.bearerId;

        if (!bearerId && temporaryTravellerData.cardSerial) {
          try {
            const claimedCard = await travelCardClaimAnonymous.mutateAsync({
              data: {
                card_serial: temporaryTravellerData.cardSerial,
                label: temporaryTravellerData.name.slice(0, 100),
              },
            });
            bearerId = claimedCard.bobcat_mtb_bearer_id;
          } catch (error) {
            console.error(error);
            toast.error(
              {
                title: formatMessage({
                  defaultMessage: 'Kunde inte koppla kortet',
                }),
                description: getApiErrorMessage(
                  error,
                  formatMessage({ defaultMessage: 'Kunde inte koppla kortet' }),
                ),
              },
              { autoClose: 4000 },
            );
            return;
          }
        }

        if (!bearerId) {
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Ingen bärare kunde kopplas till köpet.',
              }),
            },
            { autoClose: 4000 },
          );
          return;
        }

        await purchaseTicketForExternal.mutateAsync({
          data: {
            department_id: selectedDepartment,
            manual_activation: true,
            product_set_id: selectedProductSet.product_set_id,
            reason: selectedReason,
            product_type: staticTicket ? 'static' : 'planner',
            recipients: [
              {
                recipient_name: temporaryTravellerData.name,
                bobcat_recipient: {
                  bobcat_mtb_bearer_id: bearerId,
                },
              },
            ],
          },
        });

        toast.success(
          {
            title: formatMessage({ defaultMessage: 'Biljett köpt' }),
          },
          { autoClose: 3000 },
        );
        navigate({ to: '/$tenantId/tickets', params: { tenantId } });
      } catch (error) {
        console.error(error);
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Köpet misslyckades' }),
            description: getApiErrorMessage(
              error,
              formatMessage({ defaultMessage: 'Köpet misslyckades' }),
            ),
          },
          { autoClose: 3000 },
        );
      }
      return;
    }

    if (!selectedTraveller || !selectedBearer) {
      return;
    }

    const collectionMemberId = (
      selectedTraveller.memberships.find(
        (membership) =>
          membership.department_id === selectedDepartment && membership.active,
      ) ?? selectedTraveller.memberships.find((membership) => membership.active)
    )?.collection_member_id;

    if (!collectionMemberId) {
      console.error('Collection member ID not found');
      toast.error(
        {
          title: formatMessage({ defaultMessage: 'Något gick fel' }),
        },
        { autoClose: 3000 },
      );
      return;
    }

    purchaseTicket.mutate(
      {
        data: {
          department_id: selectedDepartment,
          manual_activation: true,
          product_set_id: selectedProductSet.product_set_id,
          reason: selectedReason,
          product_type: staticTicket ? 'static' : 'planner',
          send_email_notification: sendEmailNotification,
          recipients: [
            {
              collection_member_id: collectionMemberId,
              bobcat_recipient: {
                bobcat_mtb_bearer_id: selectedBearer,
              },
            },
          ],
        },
      },
      {
        onSuccess: async () => {
          if (saveBobcatEmail && bobcatEmail) {
            try {
              await linkTravellerBobcatAccount.mutateAsync({
                travellerID: selectedTraveller.traveller.id,
                data: { bobcat_private_email: bobcatEmail },
              });
              queryClient.invalidateQueries({
                queryKey: getGetTravellerByIDQueryKey(
                  selectedTraveller.traveller.id,
                ),
              });
              queryClient.invalidateQueries({
                queryKey: getGetTravellerBearersQueryKey(
                  selectedTraveller.traveller.id,
                ),
              });
              queryClient.invalidateQueries({
                queryKey:
                  getGetTravellersForDepartmentQueryKey(selectedDepartment),
              });
              queryClient.invalidateQueries({
                queryKey: getGetTravellersPerOrganizationQueryKey(),
              });
            } catch (error) {
              console.error('Failed to update traveller email:', error);
            }
          }

          toast.success(
            {
              title: formatMessage({ defaultMessage: 'Biljett köpt' }),
            },
            { autoClose: 3000 },
          );
          navigate({ to: '/$tenantId/tickets', params: { tenantId } });
        },
        onError: (error) => {
          console.error(error);
          toast.error(
            {
              title: formatMessage({ defaultMessage: 'Köpet misslyckades' }),
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    selectedProductSet,
    selectedDepartment,
    isPurchasing,
    purchaseTicket,
    purchaseTicketForExternal,
    travelCardClaimAnonymous,
    temporaryTravellerData,
    selectedTraveller,
    selectedBearer,
    selectedReason,
    staticTicket,
    formatMessage,
    navigate,
    tenantId,
    saveBobcatEmail,
    bobcatEmail,
    linkTravellerBobcatAccount,
    queryClient,
    sendEmailNotification,
  ]);

  if (canBuyTickets === false) {
    return (
      <div className="grow overflow-y-auto">
        <DocumentHead
          titleKey="page.buyTicket.title"
          descriptionKey="page.buyTicket.description"
        />
        <Header title={<FormattedMessage defaultMessage="Köp biljett" />} />
        <CenteredColumn>
          <p className="rounded-md border border-red-700 bg-red-100 p-4 text-red-700">
            <FormattedMessage defaultMessage="Du har inte behörighet att köpa biljetter." />
          </p>
        </CenteredColumn>
      </div>
    );
  }

  return (
    <div className="grow overflow-y-auto">
      <DocumentHead
        titleKey="page.buyTicket.title"
        descriptionKey="page.buyTicket.description"
      />
      <Header title={<FormattedMessage defaultMessage="Köp biljett" />} />

      <CenteredColumn>
        <OrganizationCreditInfo />
        <TravellerSelection
          initialTravellerId={travellerId}
          selectedTraveller={selectedTraveller}
          onTravellerSelect={setSelectedTraveller}
          selectedBearer={selectedBearer}
          onBearerSelect={setSelectedBearer}
          isTemporaryTraveller={isTemporaryTraveller}
          onTemporaryTravellerModeChange={(isTemp) => {
            setIsTemporaryTraveller(isTemp);
            setSelectedBearer('');
            setBobcatEmail('');
            setSaveBobcatEmail(true);
            if (!isTemp) {
              setTemporaryTravellerData(undefined);
            }
          }}
          onTemporaryTravellerDataChange={setTemporaryTravellerData}
          onBobcatEmailChange={setBobcatEmail}
          onSaveBobcatEmailChange={setSaveBobcatEmail}
        />
        <TicketSelection
          selectedProductSet={selectedProductSet}
          setSelectedProductSet={setSelectedProductSet}
          staticTicket={staticTicket}
          setStaticTicket={setStaticTicket}
        />
        <PaymentSelection
          selectedTraveller={selectedTraveller}
          reason={selectedReason}
          setReason={setSelectedReason}
          selectedDepartmentId={selectedDepartment}
          setDepartmentId={setSelectedDepartment}
          isTemporaryTraveller={isTemporaryTraveller}
        />

        {!isTemporaryTraveller && (
          <CheckBox
            id="send-email-notification"
            label={formatMessage({
              defaultMessage: 'Skicka e-post till resenären om biljetten',
            })}
            description={formatMessage({
              defaultMessage:
                'Om vald, kommer resenären få ett e-postmeddelande med information om den köpta biljetten',
            })}
            checked={sendEmailNotification}
            onClick={() => {
              setSendEmailNotification(!sendEmailNotification);
            }}
          />
        )}

        <div className="flex flex-col gap-2">
          {creditOverLimit && (
            <ErrorBanner>
              <FormattedMessage defaultMessage="Organisationens kreditgräns överskrids av detta köp och kan därför inte godkännas." />
            </ErrorBanner>
          )}

          {!selectedReason && (
            <ErrorBanner>
              <FormattedMessage defaultMessage="Du måste ange syfte med resan." />
            </ErrorBanner>
          )}

          {!selectedTraveller && !isTemporaryTraveller && (
            <ErrorBanner>
              <FormattedMessage defaultMessage="Du måste välja en resenär." />
            </ErrorBanner>
          )}

          <Button
            startIcon={<IconCheck />}
            variant="filled"
            disabled={
              (isTemporaryTraveller
                ? !temporaryTravellerData
                : !selectedTraveller || !selectedBearer) ||
              !selectedProductSet ||
              !selectedDepartment ||
              !selectedReason ||
              creditOverLimit ||
              isPurchasing
            }
            onClick={handlePurchaseTicket}
            loading={isPurchasing}
            className="w-full"
          >
            <FormattedMessage defaultMessage="Genomför biljettköp" />
            {selectedProductSet
              ? ` (${Math.round(selectedProductSet.amount * 100) / 100} ${selectedProductSet.currency})`
              : ''}
          </Button>
        </div>
      </CenteredColumn>
    </div>
  );
}
